import { useState, useEffect } from "react";
import "@/App.css";


const HEADER_IMAGE = "/images/header.webp";

// Section Images
const IMAGES = {
  gestaltberatung: "/images/gestaltberatung.webp",
  anliegen: "/images/anliegen.webp",
  haltung: "/images/haltung.webp",
  ueberMich: "/images/ueber-mich.webp",
  kontakt: "/images/kontakt.webp",
};


// Navigation Component
const Navigation = ({ activeSection, onNavigate }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const navItems = [
    { id: "gestaltberatung", label: "Was ist Gestaltberatung" },
    { id: "anliegen", label: "Mögliche Anliegen" },
    { id: "haltung", label: "Meine Haltung" },
    { id: "ueber-mich", label: "Über mich" },
    { id: "rahmen", label: "Rahmen/Ablauf" },
    { id: "kontakt", label: "Kontakt" },
  ];

  return (
    <nav className="navigation" data-testid="main-navigation">
      <div className="nav-container">
        <button 
          className="mobile-menu-btn"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-btn"
          aria-label="Menü öffnen"
        >
          <span className={`hamburger ${mobileMenuOpen ? 'open' : ''}`}></span>
        </button>
        <ul className={`nav-list ${mobileMenuOpen ? 'open' : ''}`}>
          {navItems.map((item) => (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                className={activeSection === item.id ? "active" : ""}
                onClick={(e) => {
                  e.preventDefault();
                  onNavigate(item.id);
                  setMobileMenuOpen(false);
                }}
                data-testid={`nav-${item.id}`}
              >
                {item.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => (
  <section className="hero-section" data-testid="hero-section">
    <div className="hero-image-container">
      <img 
        src={HEADER_IMAGE} 
        alt="Birkenwald - Gestaltberatung Nora Hillen" 
        className="hero-image"
      />
    </div>
    <div className="hero-title-wrapper">
      <h1 className="hero-title" data-testid="hero-title">
        Gestaltberatung Nora Hillen
      </h1>
      <img src={IMAGES.gestaltberatung} alt="Gestaltberatung Illustration" className="hero-knaeul-image" />
    </div>
  </section>
);

// Section Component
const Section = ({ id, title, children, className = "" }) => (
  <section id={id} className={`content-section ${className}`} data-testid={`section-${id}`}>
    <div className="section-container">
      {title && <h2 className="section-title">{title}</h2>}
      {children}
    </div>
  </section>
);

// Was ist Gestaltberatung Section
const GestaltberatungSection = () => (
  <Section id="gestaltberatung" title="Was ist Gestaltberatung/Gestalttherapie">
    <div className="text-content">
      <p>
        Gestaltberatung bietet einen Raum, um die eigene innere Erfahrung zu erkunden. 
        Im Mittelpunkt stehen das gegenwärtige Erleben und die gemeinsame Arbeit im Hier und Jetzt.
      </p>
      <p>
        Die Begegnung zwischen Berater*in und Klient*in dient als Experimentierfeld: 
        Verhaltensmuster, wiederkehrende Themen und Beziehungsdynamiken werden im Verlauf 
        sichtbar und verstehbar.
      </p>
      <p>
        Auch wenn wir im Hier und Jetzt arbeiten, beziehen wir Erfahrungen aus der 
        Vergangenheit mit ein. Möglichweise wurden Verhaltensmuster erlernt, die damals 
        Sinn machten und heute nicht mehr als Lösung zu den Anforderungen passen.
      </p>
      <p>
        Wenn wir verstehen, was uns in den immer gleichen Bahnen hält, können wir lernen, 
        andere Wege auszuprobieren. Die Freiheit zu wählen, wie wir reagieren wollen, 
        ermöglicht es uns, Gestalter*in unseres eigenen Lebens zu sein.
      </p>
      <p>
        Neben dem Gespräch können auch Körperempfindungen, Haltung, Gestik, Mimik und 
        Stimmung eine Rolle spielen. Dabei schauen wir gemeinsam wie und in welchem Tempo 
        die Erkundung stimmig ist.
      </p>
      <p>
        Die Gestalthaltung ist geprägt von Offenheit, Wertfreiheit und Respekt gegenüber 
        dem, was Klient*innen mitbringen und der Art, wie sie in Beziehung treten.
      </p>
    </div>
  </Section>
);

// Mögliche Anliegen Section
const AnliegenSection = () => (
  <Section id="anliegen" title="Mögliche Anliegen">
    <div className="text-content">
      <ul className="anliegen-list">
        <li>innere Unruhe oder Erschöpfung</li>
        <li>Krisen, schwierige Lebenssituationen, Zeiten des Übergangs</li>
        <li>ein Gefühl von Isolation; die Sehnsucht nach Kontakt zu anderen Menschen</li>
        <li>das Gefühl, immer wieder in ähnliche, unbefriedigende oder emotional-belastende Situationen hineinzugeraten</li>
        <li>das Gefühl, oft missverstanden zu werden</li>
        <li>ein allgemeines Gefühl von Unzufriedenheit mit der eigenen Situation; Selbstzweifel oder Selbstwertprobleme</li>
        <li>Entscheidungs- oder Orientierungsschwierigkeiten</li>
        <li>der Wunsch nach mehr Lebendigkeit und Klarheit</li>
      </ul>
    </div>
  </Section>
);

// Bild-Sektion zwischen Anliegen und Haltung - entfernt
const ImageDividerSection = () => null;

// Meine Haltung Section
const HaltungSection = () => (
  <Section id="haltung" title="Meine Haltung">
    <div className="text-content">
      <p>
        Meine eigenen Erfahrungen als Klientin in der Gestalttherapie haben mich dazu bewegt, 
        selbst als Gestalttherapeutin arbeiten zu wollen.
      </p>
      <p>
        Was mich an der Gestaltarbeit besonders berührt, ist ihre konsequente Ausrichtung 
        am eigenen inneren Tempo und an der eigenen Wahrnehmung. Beides wird ernst genommen 
        und bildet den Ausgangspunkt für die persönliche Orientierung in der Welt. Aus diesem 
        Kontakt heraus entstehen Lebendigkeit und die Freude daran, das eigene Leben aktiv zu gestalten.
      </p>
      <p>
        Ich gehe davon aus, dass meine Klient*innen bereits in sich tragen, was sie brauchen, 
        sich wünschen und was sie lebendig macht. Manchmal ist der Zugang zu dieser inneren 
        Wahrheit verschüttet. Mit meiner Begleitung möchte ich dir zu mehr Klarheit verhelfen. 
        Gemeinsam können wir schauen, was den Weg verstellt und wie du deine Ressourcen nutzen 
        kannst, um ihn wieder freizuräumen.
      </p>
      <p>
        Als Gegenüber bin ich empathisch, offen und humorvoll. Ich gestalte einen warmen, 
        vertrauensvollen Raum. So kann ich dir Stütze und zugleich Spiegel sein.
      </p>
    </div>
  </Section>
);

// Über mich Section
const UeberMichSection = () => (
  <Section id="ueber-mich" title="Über mich">
    <div className="text-content">
      <p className="intro-text full-width">
        Ich lebe seit etwa 15 Jahren in Leipzig, esse gerne Yogurette aus dem Eisfach, 
        liebe Birkenwälder und meine Katze Kiwi.
      </p>
      
      <div className="timeline-with-image">
        <div className="timeline">
          <div className="timeline-item">
            <span className="year">2012</span>
            <span className="description">B.A. in Erziehungswissenschaften an der Uni Marburg</span>
          </div>
          <div className="timeline-item">
            <span className="year">2012 – 2014</span>
            <span className="description">Weiterbildung zur integrativen Tanzpädagogin (iTP) und Wildnispädagogin (Natur- u. Wildnisschule Teutoburger Wald)</span>
          </div>
          <div className="timeline-item">
            <span className="year">2015 – 2019</span>
            <span className="description">Mitinhaberin und Co-Managment eines Kollektivbetriebs</span>
          </div>
          <div className="timeline-item">
            <span className="year">2020 – 2024</span>
            <span className="description">Einzelfallbetreuung von Jugendlichen mit psychischen Herausforderungen</span>
          </div>
          <div className="timeline-item">
            <span className="year">2021 – 2025</span>
            <span className="description">Weiterbildung in Gestalttherapie und -beratung am IGG Berlin</span>
          </div>
          <div className="timeline-item">
            <span className="year">vsl. 10/2026</span>
            <span className="description">Zulassung Heilpraktikerin für Psychotherapie nach HeilprGDV 1</span>
          </div>
        </div>
        <div className="section-image-container portrait">
          <img src={IMAGES.ueberMich} alt="Nora Hillen" className="section-image" />
        </div>
      </div>
      
      <p className="supervision-note full-width">
        Meine Arbeit reflektiere ich regelmäßig in der Supervision und bin Teil einer 
        Intervisionsgruppe, in der wir unter Kolleg*innen einzelne Fälle, anonymisiert 
        und absolut vertraulich, besprechen.
      </p>
    </div>
  </Section>
);

// Rahmen/Ablauf Section
const RahmenSection = () => (
  <Section id="rahmen" title="Rahmen/Ablauf">
    <div className="text-content">
      <div className="pricing-card">
        <h3>Einzelberatung</h3>
        <p className="duration">50 Minuten</p>
        <p className="price">70€</p>
      </div>
      
      <p>
        <strong>Das Erstgespräch ist kostenlos.</strong> Es dient dem gegenseitigen 
        Kennenlernen und der Klärung, ob eine Zusammenarbeit sinnvoll ist.
      </p>
      
      <p>
        Mein Angebot wird nicht von der gesetzlichen Krankenkasse übernommen und richtet 
        sich daher an Selbstzahlende.
      </p>
      
      <p className="welcome-note">
        Alle Menschen sind unabhängig von ihrer Herkunft, Sexualität oder Identität herzlich willkommen.
      </p>
    </div>
  </Section>
);

// Kontakt Section
const KontaktSection = () => (
  <Section id="kontakt" title="Kontakt">
    <div className="kontakt-layout">
      <div className="kontakt-image-container">
        <img src={IMAGES.kontakt} alt="Praxisraum" className="kontakt-image" />
      </div>
      <div className="kontakt-content">
        <p>Ich bin per E-Mail oder Telefon erreichbar:</p>
        
        <div className="contact-info">
          <div className="contact-item">
            <span className="contact-label">E-Mail:</span>
            <a href="mailto:norahillen@proton.me" data-testid="email-link">norahillen@proton.me</a>
          </div>
          <div className="contact-item">
            <span className="contact-label">Telefon:</span>
            <a href="tel:+4915205199010" data-testid="phone-link">01520 5199010</a>
          </div>
        </div>
        
        <p className="datenschutz-hinweis">
          <strong>Hinweis zum Datenschutz:</strong> Kontaktdaten, die mir per E-Mail oder 
          Telefon übermittelt werden, dienen ausschließlich zur Beantwortung der Anfrage 
          und werden nicht an Dritte weitergegeben.
        </p>
        
        <div className="praxis-info">
          <h3>Praxisraum</h3>
          <address>
            Praxiskollektiv<br />
            Klingenstraße 10<br />
            04229 Leipzig/Plagwitz
          </address>
        </div>
      </div>
    </div>
  </Section>
);

// Footer
const Footer = () => (
  <footer className="footer" data-testid="footer">
    <div className="footer-container">
      <p className="legal-notice">
        <strong>Gesetzlicher Hinweis:</strong> Ich biete keine Heilbehandlung im Sinne des 
        Heilpraktikergesetzes an. Meine Begleitung ersetzt keine ärztliche oder 
        psychotherapeutische Behandlung.
      </p>
      <div className="footer-links">
        <a href="/datenschutz.html" data-testid="datenschutz-link">Datenschutz</a>
        <span className="divider">|</span>
        <a href="/impressum.html" data-testid="impressum-link">Impressum</a>
      </div>
      <p className="copyright">© {new Date().getFullYear()} Gestaltberatung Nora Hillen</p>
    </div>
  </footer>
);

function App() {
  const [activeSection, setActiveSection] = useState("");

  const handleNavigate = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll(".content-section");
      let current = "";
      
      sections.forEach((section) => {
        const sectionTop = section.offsetTop;
        if (window.scrollY >= sectionTop - 200) {
          current = section.getAttribute("id");
        }
      });
      
      setActiveSection(current);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="app" data-testid="app-container">
      <Navigation activeSection={activeSection} onNavigate={handleNavigate} />
      <main>
        <HeroSection />
        <GestaltberatungSection />
        <AnliegenSection />
        <ImageDividerSection />
        <HaltungSection />
        <UeberMichSection />
        <RahmenSection />
        <KontaktSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
